<template>
  <transition name="uber-modal">
    <div v-if="isOpen" class="uber-modal-overlay" @click.self="$emit('close')">
      <div class="uber-modal">
        <!-- Header -->
        <div class="modal-header">
          <button class="close-btn" @click="$emit('close')">
            <i class="fas fa-times"></i>
          </button>
          <h2>{{ t.adjustOrder }}</h2>
          <div class="header-spacer"></div>
        </div>

        <!-- Uber Badge & Order ID -->
        <div class="uber-header">
          <div class="uber-logo">
            <img src="/images/Uber_Eats_2020_logo.svg.png" alt="Uber Eats" />
          </div>
          <div class="order-id">#{{ order.uberDisplayId || order.orderNumber }}</div>
        </div>

        <!-- Order Items Summary -->
        <div class="items-summary" @click="showItems = !showItems">
          <div class="summary-left">
            <i class="fas fa-shopping-bag"></i>
            <span>{{ itemCount }} {{ t.items }}</span>
          </div>
          <div class="summary-right">
            <span class="total">{{ orderTotal.toFixed(2) }}</span>
            <i :class="['fas', showItems ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
        </div>

        <!-- Expanded Items List -->
        <div v-if="showItems" class="items-list">
          <div v-for="(item, index) in orderItems" :key="index" class="item-row">
            <span class="item-qty">{{ item.quantity || 1 }}x</span>
            <div class="item-details">
              <span class="item-name">{{ item.name }}</span>
              <span v-if="item.notes" class="item-notes">{{ item.notes }}</span>
            </div>
            <span class="item-price">{{ (item.price || 0).toFixed(2) }}</span>
          </div>
        </div>

        <!-- Menu Options -->
        <div class="menu-options">
          <!-- Items out of stock -->
          <button class="menu-option" @click="$emit('out-of-stock')">
            <div class="option-left">
              <i class="fas fa-box-open"></i>
              <span>{{ t.itemsOutOfStock }}</span>
            </div>
            <i class="fas fa-chevron-right"></i>
          </button>

          <!-- Contact Customer -->
          <button class="menu-option" @click="contactCustomer">
            <div class="option-left">
              <i class="fas fa-phone"></i>
              <span>{{ t.contactCustomer }}</span>
            </div>
            <i class="fas fa-chevron-right"></i>
          </button>

          <!-- Customer Info (shown when expanded) -->
          <div v-if="showCustomerInfo" class="customer-info-panel">
            <div class="info-row" @click="callCustomer">
              <i class="fas fa-phone"></i>
              <span class="phone-number">{{ order.customer?.phone || t.notAvailable }}</span>
            </div>
            <div v-if="order.customer?.phoneCode" class="info-row pin-row">
              <i class="fas fa-key"></i>
              <span class="pin-label">PIN:</span>
              <span class="pin-code">{{ order.customer.phoneCode }}</span>
            </div>
            <div class="info-row">
              <i class="fas fa-user"></i>
              <span>{{ order.customer?.name || t.uberCustomer }}</span>
            </div>
            <div v-if="order.deliveryAddress" class="info-row">
              <i class="fas fa-map-marker-alt"></i>
              <span>{{ order.deliveryAddress }}</span>
            </div>
          </div>

          <!-- Cancel Order -->
          <button class="menu-option danger" @click="showCancelConfirm = true">
            <div class="option-left">
              <i class="fas fa-ban"></i>
              <span>{{ t.cancelOrder }}</span>
            </div>
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <!-- Cancel Confirmation -->
        <div v-if="showCancelConfirm" class="cancel-confirm-section">
          <div class="section-title">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ t.selectCancelReason }}</span>
          </div>
          <div class="cancel-reasons">
            <button
              v-for="reason in translatedCancelReasons"
              :key="reason.id"
              :class="['reason-btn', { active: selectedCancelReason === reason.id }]"
              @click="selectedCancelReason = reason.id"
            >
              {{ reason.label }}
            </button>
          </div>
          <div class="cancel-actions">
            <button class="back-btn" @click="showCancelConfirm = false; selectedCancelReason = null">
              <i class="fas fa-arrow-left"></i>
              {{ t.back }}
            </button>
            <button
              class="confirm-cancel-btn"
              :disabled="!selectedCancelReason"
              @click="confirmCancel"
            >
              <i class="fas fa-ban"></i>
              {{ t.confirmCancel }}
            </button>
          </div>
        </div>

        <!-- Order Status -->
        <div class="order-status">
          <div class="status-badge" :class="statusClass">
            <i :class="statusIcon"></i>
            {{ statusText }}
          </div>
          <div v-if="order.prepTime" class="prep-time">
            <i class="fas fa-clock"></i>
            {{ order.prepTime }} {{ t.minPrepTime }}
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'UberOrderDetailsModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: () => ({})
    },
    language: {
      type: String,
      default: 'de'
    }
  },
  data() {
    return {
      showItems: false,
      showCustomerInfo: false,
      showCancelConfirm: false,
      selectedCancelReason: null,
      translations: {
        de: {
          adjustOrder: 'Bestellung anpassen',
          items: 'Artikel',
          itemsOutOfStock: 'Artikel nicht verfügbar',
          contactCustomer: 'Kunde kontaktieren',
          cancelOrder: 'Bestellung stornieren',
          selectCancelReason: 'Stornierungsgrund wählen',
          back: 'Zurück',
          confirmCancel: 'Stornieren',
          notAvailable: 'Nicht verfügbar',
          uberCustomer: 'Uber Kunde',
          minPrepTime: 'min Zubereitungszeit',
          statusAccepted: 'Angenommen',
          statusPreparing: 'In Zubereitung',
          statusReady: 'Bereit',
          statusCompleted: 'Abgeschlossen',
          reasonItemsUnavailable: 'Artikel nicht verfügbar',
          reasonKitchenBusy: 'Küche zu beschäftigt',
          reasonCustomerRequest: 'Kunde hat storniert',
          reasonOther: 'Anderer Grund'
        },
        ar: {
          adjustOrder: 'تعديل الطلب',
          items: 'عناصر',
          itemsOutOfStock: 'عناصر غير متوفرة',
          contactCustomer: 'اتصل بالعميل',
          cancelOrder: 'إلغاء الطلب',
          selectCancelReason: 'اختر سبب الإلغاء',
          back: 'رجوع',
          confirmCancel: 'إلغاء',
          notAvailable: 'غير متوفر',
          uberCustomer: 'عميل أوبر',
          minPrepTime: 'دقيقة وقت التحضير',
          statusAccepted: 'مقبول',
          statusPreparing: 'قيد التحضير',
          statusReady: 'جاهز',
          statusCompleted: 'مكتمل',
          reasonItemsUnavailable: 'عناصر غير متوفرة',
          reasonKitchenBusy: 'المطبخ مشغول',
          reasonCustomerRequest: 'طلب العميل الإلغاء',
          reasonOther: 'سبب آخر'
        }
      }
    };
  },
  computed: {
    currentLanguage() {
      return this.$store?.state?.language || 'de';
    },
    t() {
      return this.translations[this.currentLanguage] || this.translations.de;
    },
    orderItems() {
      return this.order.items || [];
    },
    itemCount() {
      return this.orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    },
    orderTotal() {
      return this.orderItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
    },
    translatedCancelReasons() {
      return [
        { id: 'items_unavailable', label: this.t.reasonItemsUnavailable },
        { id: 'kitchen_busy', label: this.t.reasonKitchenBusy },
        { id: 'customer_request', label: this.t.reasonCustomerRequest },
        { id: 'other', label: this.t.reasonOther }
      ];
    },
    statusClass() {
      const status = this.order.status;
      if (status === 'new' || status === 'accepted') return 'status-new';
      if (status === 'preparing') return 'status-preparing';
      if (status === 'ready') return 'status-ready';
      if (status === 'completed') return 'status-completed';
      return 'status-new';
    },
    statusIcon() {
      const status = this.order.status;
      if (status === 'new' || status === 'accepted') return 'fas fa-receipt';
      if (status === 'preparing') return 'fas fa-fire';
      if (status === 'ready') return 'fas fa-check-circle';
      if (status === 'completed') return 'fas fa-flag-checkered';
      return 'fas fa-receipt';
    },
    statusText() {
      const status = this.order.status;
      if (status === 'new' || status === 'accepted') return this.t.statusAccepted;
      if (status === 'preparing') return this.t.statusPreparing;
      if (status === 'ready') return this.t.statusReady;
      if (status === 'completed') return this.t.statusCompleted;
      return this.t.statusAccepted;
    }
  },
  methods: {
    contactCustomer() {
      this.showCustomerInfo = !this.showCustomerInfo;
    },
    callCustomer() {
      const phone = this.order.customer?.phone;
      if (phone) {
        window.location.href = `tel:${phone.replace(/\s/g, '')}`;
      }
    },
    confirmCancel() {
      this.$emit('cancel', {
        orderId: this.order.orderId || this.order.id,
        reason: this.selectedCancelReason
      });
      this.showCancelConfirm = false;
      this.selectedCancelReason = null;
    }
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.showItems = false;
        this.showCustomerInfo = false;
        this.showCancelConfirm = false;
        this.selectedCancelReason = null;
      }
    }
  }
};
</script>

<style scoped>
/* Modal Transitions */
.uber-modal-enter-active,
.uber-modal-leave-active {
  transition: opacity 0.3s ease;
}

.uber-modal-enter-active .uber-modal,
.uber-modal-leave-active .uber-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.uber-modal-enter,
.uber-modal-leave-to {
  opacity: 0;
}

.uber-modal-enter .uber-modal,
.uber-modal-leave-to .uber-modal {
  transform: translateY(20px);
  opacity: 0;
}

/* Overlay */
.uber-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

/* Modal Container */
.uber-modal {
  background: var(--ods-surface);
  border-radius: var(--ods-radius-lg);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ods-line);
}

.modal-header h2 {
  font-size: 17px;
  font-weight: 600;
  color: var(--ods-ink);
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 18px;
  color: var(--ods-ink);
  cursor: pointer;
}

.close-btn:hover {
  color: var(--ods-ink-2);
}

.header-spacer {
  width: 32px;
}

/* Uber Header */
.uber-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f7f7f7;
}

.uber-logo img {
  height: 28px;
}

.order-id {
  font-size: 20px;
  font-weight: 700;
  color: #000;
}

/* Items Summary */
.items-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ods-line);
  cursor: pointer;
}

.items-summary:hover {
  background: var(--ods-surface);
}

.summary-left {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--ods-ink);
  font-size: 15px;
  font-weight: 500;
}

.summary-left i {
  color: var(--ods-ink-2);
}

.summary-right {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--ods-ink);
}

.summary-right .total {
  font-weight: 600;
  color: #30d158;
}

.summary-right .total::before {
  content: '\20AC';
  margin-right: 2px;
}

.summary-right i {
  color: var(--ods-ink-2);
  font-size: 12px;
}

/* Items List */
.items-list {
  padding: 0 20px 16px;
  background: var(--ods-surface);
}

.item-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--ods-line);
}

.item-row:last-child {
  border-bottom: none;
}

.item-qty {
  font-size: 14px;
  font-weight: 600;
  color: var(--ods-ink);
  min-width: 28px;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--ods-ink);
}

.item-notes {
  font-size: 12px;
  color: var(--ods-accent-ink);
}

.item-price {
  font-size: 14px;
  color: var(--ods-ink-2);
}

.item-price::before {
  content: '\20AC';
  margin-right: 2px;
}

/* Menu Options */
.menu-options {
  padding: 8px 0;
}

.menu-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  border-bottom: 1px solid var(--ods-line);
  cursor: pointer;
  text-align: left;
}

.menu-option:hover {
  background: var(--ods-surface);
}

.option-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.option-left i {
  width: 20px;
  color: var(--ods-ink);
  font-size: 16px;
}

.option-left span {
  font-size: 15px;
  color: var(--ods-ink);
}

.menu-option > i {
  color: var(--ods-line);
  font-size: 12px;
}

.menu-option.danger .option-left i,
.menu-option.danger .option-left span {
  color: #ff453a;
}

/* Customer Info Panel */
.customer-info-panel {
  padding: 12px 20px;
  background: var(--ods-surface);
  border-bottom: 1px solid var(--ods-line);
}

.customer-info-panel .info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
}

.customer-info-panel .info-row i {
  width: 18px;
  color: var(--ods-ink-2);
  font-size: 14px;
}

.customer-info-panel .info-row span {
  font-size: 14px;
  color: var(--ods-ink);
}

.customer-info-panel .phone-number {
  color: var(--ods-accent-ink) !important;
  cursor: pointer;
}

.customer-info-panel .pin-row {
  background: rgba(255, 159, 10, 0.15);
  border-radius: var(--ods-radius);
  padding: 10px 12px !important;
  margin: 4px 0;
}

.customer-info-panel .pin-row i {
  color: var(--ods-accent-ink) !important;
}

.customer-info-panel .pin-label {
  color: var(--ods-ink-2) !important;
  margin-right: 8px;
}

.customer-info-panel .pin-code {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: var(--ods-accent-ink) !important;
  letter-spacing: 2px;
}

/* Cancel Confirm Section */
.cancel-confirm-section {
  padding: 16px 20px;
  background: var(--ods-surface);
  border-top: 1px solid var(--ods-line);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ff453a;
  margin-bottom: 12px;
}

.cancel-reasons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.reason-btn {
  padding: 12px 16px;
  background: var(--ods-surface-2);
  border: none;
  border-radius: var(--ods-radius);
  color: var(--ods-ink);
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}

.reason-btn:hover {
  background: #48484a;
}

.reason-btn.active {
  background: #ff453a;
  color: var(--ods-ink);
}

.cancel-actions {
  display: flex;
  gap: 10px;
}

.cancel-actions button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: var(--ods-radius);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.back-btn {
  background: var(--ods-surface-2);
  color: var(--ods-ink);
}

.back-btn:hover {
  background: #48484a;
}

.confirm-cancel-btn {
  background: #ff453a;
  color: var(--ods-ink);
}

.confirm-cancel-btn:disabled {
  background: var(--ods-surface-2);
  color: var(--ods-line);
  cursor: not-allowed;
}

.confirm-cancel-btn:not(:disabled):hover {
  background: #ff5147;
}

/* Order Status */
.order-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--ods-surface);
  border-radius: 0 0 var(--ods-radius-lg) var(--ods-radius-lg);
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: var(--ods-radius-lg);
  font-size: 13px;
  font-weight: 600;
}

.status-new {
  background: rgba(48, 209, 88, 0.15);
  color: #30d158;
}

.status-preparing {
  background: rgba(255, 159, 10, 0.15);
  color: var(--ods-accent-ink);
}

.status-ready {
  background: rgba(10, 132, 255, 0.15);
  color: var(--ods-accent-ink);
}

.status-completed {
  background: rgba(191, 90, 242, 0.15);
  color: #bf5af2;
}

.prep-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--ods-ink-2);
}

/* Scrollbar */
.uber-modal::-webkit-scrollbar {
  width: 6px;
}

.uber-modal::-webkit-scrollbar-track {
  background: transparent;
}

.uber-modal::-webkit-scrollbar-thumb {
  background: var(--ods-surface-3);
  border-radius: 3px;
}

.uber-modal::-webkit-scrollbar-thumb:hover {
  background: var(--ods-surface-3);
}
</style>
